import React from "react";
import { styled, useTheme } from "@mui/system";
import dynamic from "next/dynamic";
import { Typography, FormControl, FormControlLabel, FormLabel, IconButton, Radio, RadioGroup } from "@mui/material";
import Popover from "@mui/material/Popover";
import Chip from "@mui/material/Chip";
import StateInterface from "../../redux-magic/state-interface";
import { useRouter } from "next/router";
import PreparePriceString from "../../lib/PreparePriceString";
import { SearchFilterOptionValueRange } from "../../redux-magic/sub-interfaces/search-types";
import BeegruButton from "../common-components/buttons/BeegruButton";
import {
	updateSearchFilterActiveOptionsThunk,
	updateSearchFilterAppliedThunk,
	updateSearchFilterIsAccordionOpenThunk,
	updateSearchFilterSelectedOptionsThunk,
	updateSearchFilterVisibilityThunk,
} from "../../redux-magic/thunks";
import PpsfFilterGenertateUrl from "../../lib/PpsfFilterGenertateUrl";

const CloseIcon = dynamic(() => import("@mui/icons-material/Close"));
const KeyboardArrowDownIcon = dynamic(() => import("@mui/icons-material/KeyboardArrowDown"));
const KeyboardArrowUpIcon = dynamic(() => import("@mui/icons-material/KeyboardArrowUp"));
const BudgetComponent = dynamic(() => import("./").then((mod) => mod.Budget));
const AreaMobile = dynamic(() => import("./SearchMobile/AreaMobile"));
const BedroomsMobile = dynamic(() => import("./SearchMobile/BedroomsMobile"));
const NoOfSeatsMobile = dynamic(() => import("./SearchMobile/NoOfSeatsMobile"));
const PropertyCategoryMobile = dynamic(() => import("./SearchMobile/PropertyCategoryMobile"));
const PropertyTypeMobile = dynamic(() => import("./SearchMobile/PropertyTypeMobile"));
const ServiceCategoryMobile = dynamic(() => import("./SearchMobile/ServiceCategoryMobile"));
const ServiceSubTypeMobile = dynamic(() => import("./SearchMobile/ServiceSubTypeMobile"));
const TransactionType = dynamic(() => import("./SearchMobile/TransactionTypeMobile"));

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	gap: "1rem",
	// padding: "3.125rem 0rem",
	padding: "1.5rem 0rem 1.5rem 0rem",
	background: "#ffffff",
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
		alignItems: "flex-start",
		gap: "1rem",
	},
}));

const PropertyChips = styled(Chip)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	padding: "0rem 0.25rem 0rem 0.5rem",
	height: "1.75rem",
	gap: "0.25rem",
	borderRadius: "8px",
	fontWeight: 500,
	color: theme.palette.text.primary,
	background: theme.palette.background.paper,
	"& .MuiChip-label": {
		padding: "0rem",
		fontWeight: 400,
	},
	"& .MuiChip-deleteIcon": {
		margin: "0rem 0rem 0rem 0rem",
		color: theme.palette.text.primary,
		fontSize: "1rem",
		fontWeight: 400,
	},
	[theme.breakpoints.down("md1220")]: { padding: "1rem" },
	[theme.breakpoints.down("sm")]: { padding: "0rem 0.25rem 0rem 0.5rem" },
})) as typeof Chip;

const TypographyHeading = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
}));

const PpsfChips = ({
	search_filter_state,
	dispatch,
}: {
	search_filter_state: StateInterface["search_filters_state"];
	dispatch: Function;
}) => {
	const theme = useTheme();
	const router = useRouter();

	let filterTypes = [
		search_filter_state.search_type.selected_options[0]?.title === "Business Profiles" ? null : "Budget",
		search_filter_state.transaction_type.selected_options.length > 0
			? search_filter_state.transaction_type.selected_options[0]?.title
			: null,
		search_filter_state.search_type.selected_options[0]?.id === "services"
			? "Service type"
			: search_filter_state.search_type.selected_options[0]?.id === "properties"
				? "Property type"
				: "Business user type",
		search_filter_state.property_category?.is_applied &&
		search_filter_state.property_type.selected_options.length > 0 &&
		[
			"apartment",
			"independent_house",
			"villa",
			"co_living",
			"row_house",
			"builder_floor_apartment",
			"penthouse",
			"farm_house",
		].includes(search_filter_state.property_type.selected_options[0]?.id)
			? "Bedrooms"
			: search_filter_state.property_type?.is_applied &&
				  search_filter_state.property_type.selected_options.length > 0 &&
				  [
						"land_residential",
						"land_commercial",
						"land_agricultural",
						"land_industrial",
						"plot",
						"plot_commercial",
						"farm_plot",
						"building_commercial",
						"shop",
						"office_space",
						"warehouse",
						"factory",
						"mining",
				  ].includes(search_filter_state.property_type.selected_options[0]?.id)
				? "Area"
				: search_filter_state.property_type?.is_applied &&
					  search_filter_state.property_type.selected_options.length > 0 &&
					  ["co_working"].includes(search_filter_state.property_type.selected_options[0]?.id)
					? "No of seats"
					: null,
	].filter(Boolean);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

	const handleChipClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
		setAnchorEl(event.currentTarget);
		setSelectedIndex(index);
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
		setSelectedIndex(null);
	};

	const openPopOver = Boolean(anchorEl);
	let budgetToDisplay = search_filter_state.budget.selected_options[0]?.value as SearchFilterOptionValueRange;

	const getStyles = (
		data: string,
		id: number,
		search_filter_state: StateInterface["search_filters_state"],
		filterTypes: number,
	) => {
		const isTransactionApplied = search_filter_state.transaction_type?.is_applied;
		const isPropertyApplied =
			search_filter_state.property_category?.is_applied ||
			search_filter_state.service_type?.is_applied ||
			search_filter_state.business_profile_type?.is_applied;
		const isBudgetApplied = search_filter_state.budget?.is_applied;
		const isBusinessTypeApplied = search_filter_state.business_profile_type?.is_applied;
		const isBedroomsApplied = search_filter_state.bedrooms_variant_2?.is_applied;
		const isAreaApplied = search_filter_state.area?.is_applied;

		let border = "1px solid rgba(0, 0, 0, 0.12)";
		let background = theme.palette.background.paper;
		let fontWeight = 400;

		if (filterTypes === 4) {
			if (id === 1 && isTransactionApplied) {
				border = "1px solid rgba(252, 128, 25, 0.48)";
				background = "rgba(252, 128, 25, 0.16)";
				fontWeight = 500;
			} else if (id === 2 && isPropertyApplied) {
				border = "1px solid rgba(252, 128, 25, 0.48)";
				background = "rgba(252, 128, 25, 0.16)";
				fontWeight = 500;
			} else if (id === 0 && isBudgetApplied) {
				border = "1px solid rgba(252, 128, 25, 0.48)";
				background = "rgba(252, 128, 25, 0.16)";
				fontWeight = 500;
			} else if (id === 3 && (isBedroomsApplied || isAreaApplied)) {
				border = "1px solid rgba(252, 128, 25, 0.48)";
				background = "rgba(252, 128, 25, 0.16)";
				fontWeight = 500;
			}
		} else if (filterTypes === 2) {
			if (id === 1 && isPropertyApplied) {
				border = "1px solid rgba(252, 128, 25, 0.48)";
				background = "rgba(252, 128, 25, 0.16)";
				fontWeight = 500;
			} else if (id === 0 && isBudgetApplied) {
				border = "1px solid rgba(252, 128, 25, 0.48)";
				background = "rgba(252, 128, 25, 0.16)";
				fontWeight = 500;
			}
		} else if (filterTypes === 1) {
			if (id === 0 && isBusinessTypeApplied) {
				border = "1px solid rgba(252, 128, 25, 0.48)";
				background = "rgba(252, 128, 25, 0.16)";
				fontWeight = 500;
			}
		}

		return { border, background, fontWeight };
	};

	const [isLoading, setIsLoading] = React.useState(false);
	const [FilterChanged, setFilterChanged] = React.useState<boolean>(false);

	const handleClickSearchButton = () => {
		if (search_filter_state.location.is_applied === false) {
			// Optionally show a location warning
		} else {
			if (isLoading) return;
			setIsLoading(true);
			setIsLoading(false);
			setFilterChanged(true);
			handlePopoverClose();
		}
	};

	const handleChangeBusinessUsers = (value: string) => {
		dispatch(updateSearchFilterAppliedThunk({ searchFilterId: "property_category", isApplied: false }));
		dispatch(updateSearchFilterAppliedThunk({ searchFilterId: "property_type", isApplied: false }));

		dispatch(updateSearchFilterVisibilityThunk({ searchFilterId: "property_category", isVisible: false }));
		dispatch(updateSearchFilterVisibilityThunk({ searchFilterId: "property_type", isVisible: false }));
		dispatch(updateSearchFilterVisibilityThunk({ searchFilterId: "property_status", isVisible: false }));

		dispatch(updateSearchFilterAppliedThunk({ searchFilterId: "service_category", isApplied: false }));
		dispatch(updateSearchFilterVisibilityThunk({ searchFilterId: "service_category", isVisible: false }));
		dispatch(updateSearchFilterAppliedThunk({ searchFilterId: "service_subtype", isApplied: false }));

		dispatch(updateSearchFilterVisibilityThunk({ searchFilterId: "service_subtype", isVisible: false }));
		dispatch(updateSearchFilterAppliedThunk({ searchFilterId: "service_type", isApplied: false }));
		dispatch(updateSearchFilterVisibilityThunk({ searchFilterId: "service_type", isVisible: false }));

		dispatch(updateSearchFilterAppliedThunk({ searchFilterId: "business_profile_type", isApplied: true }));
		dispatch(updateSearchFilterVisibilityThunk({ searchFilterId: "business_profile_type", isVisible: true }));
		dispatch(
			updateSearchFilterSelectedOptionsThunk({
				searchFilterId: "transaction_type",
				newOptions: [],
			}),
		);
		dispatch(
			updateSearchFilterAppliedThunk({
				searchFilterId: "transaction_type",
				isApplied: false,
			}),
		);
		dispatch(
			updateSearchFilterSelectedOptionsThunk({
				searchFilterId: "search_type",
				newOptions: [
					{
						id: "business_profiles",
						title: "Business Profiles",
						value: "business_profiles",
					},
				],
			}),
		);
		dispatch(
			updateSearchFilterSelectedOptionsThunk({
				searchFilterId: "business_profile_type",
				newOptions: [
					{
						id: value === undefined ? "agents" : value.toLocaleLowerCase(),
						title: value === undefined ? "Agents" : value,
						value: value === undefined ? "agents" : value.toLocaleLowerCase(),
					},
				],
			}),
		);

		const business_profile_type = search_filter_state.business_profile_type.active_options.map((item) => ({
			...item,
			is_selected: item.title === value ? true : false,
		}));

		dispatch(
			updateSearchFilterActiveOptionsThunk({
				searchFilterId: "business_profile_type",
				newOptions: business_profile_type,
			}),
		);
		dispatch(
			updateSearchFilterAppliedThunk({
				searchFilterId: "search_type",
				isApplied: true,
			}),
		);

		dispatch(
			updateSearchFilterIsAccordionOpenThunk({
				searchFilterId: "business_profile_type",
				isAccordionOpen: true,
			}),
		);
	};

	const DrawerRenderFunctions = (value: any) => {
		if (filterTypes.length === 4 || filterTypes.length === 3) {
			switch (value) {
				case 0:
					return (
						<React.Fragment>
							<div style={{ marginBottom: "1rem" }}>
								<div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
									<TypographyHeading
										variant="body1"
										sx={{ marginBottom: "0.5rem" }}
									>
										{router.locale === "ar-AE"
											? "أدخل السعر"
											: router.locale === "kn-IN"
												? "ಬಜೆಟ್ ನಮೂದಿಸಿ"
												: "Enter Budget"}
									</TypographyHeading>
									<IconButton
										size="small"
										onClick={handlePopoverClose}
									>
										<CloseIcon
											fontSize="small"
											sx={{ color: "rgba(0, 0, 0, 0.54)" }}
										/>
									</IconButton>
								</div>
								<BudgetComponent
									search_id="budget"
									dispatch={dispatch}
									budget={search_filter_state.budget}
									transaction_type={search_filter_state.transaction_type}
									search_filter_state={search_filter_state}
								/>
							</div>
							{/* <BeegruButton
								flavor="primary"
								variant="contained"
								size="medium"
								sx={{ fontSize: "1rem", alingnItems: "flex-start", height: "100%", width: "100%" }}
								onClick={handleClickSearchButton}
							>
								{router.locale === "ar-AE" ? "بحث" : router.locale === "kn-IN" ? "ಹುಡುಕಿ" : "Search"}
							</BeegruButton> */}
						</React.Fragment>
					);
				case 1:
					return (
						<React.Fragment>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "space-between",
									paddingBottom: "0.5rem",
								}}
							>
								<Typography variant="body1">What are you looking for?</Typography>
								<IconButton
									size="small"
									onClick={handlePopoverClose}
								>
									<CloseIcon
										fontSize="small"
										sx={{ color: "rgba(0, 0, 0, 0.54)" }}
									/>
								</IconButton>
							</div>
							<TransactionType
								search_id={"transaction_type"}
								transaction_type={search_filter_state.transaction_type}
								dispatch={dispatch}
								search_filter_state={search_filter_state}
							/>
						</React.Fragment>
					);
				case 2:
					return (
						<React.Fragment>
							{/* Property Filters */}
							{search_filter_state.search_type.selected_options[0]?.title === "Properties" &&
							(search_filter_state.transaction_type.selected_options[0]?.title === "Buy" ||
								search_filter_state.transaction_type.selected_options[0]?.title === "Rent") &&
							filterTypes.includes("Property type") ? (
								<div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "0.5rem" }}>
									<div style={{ padding: "0rem 0rem 0.5rem 0rem" }}>
										{search_filter_state.property_category.is_applied &&
										search_filter_state.property_category.selected_options[0]?.id !== "warehousing" ? (
											<>
												<TypographyHeading variant="body1">
													{router.locale === "ar-AE"
														? "اختر نوع العقار"
														: router.locale === "kn-IN"
															? "ಆಸ್ತಿ ಪ್ರಕಾರವನ್ನು ಆಯ್ಕೆಮಾಡಿ"
															: "Select property type"}
												</TypographyHeading>
												<PropertyTypeMobile
													search_id="property_type"
													property_category_value={
														search_filter_state.property_category?.selected_options[0]?.value as string
													}
													dispatch={dispatch}
													property_type={search_filter_state.property_type}
													TabValue={value}
													search_filter_state={search_filter_state}
												/>
											</>
										) : null}
									</div>
									{/* <BeegruButton
										flavor="primary"
										variant="contained"
										size="medium"
										sx={{ fontSize: "1rem", alingnItems: "flex-start", height: "100%", width: "100%" }}
										onClick={handleClickSearchButton}
									>
										{router.locale === "ar-AE" ? "بحث" : router.locale === "kn-IN" ? "ಹುಡುಕಿ" : "Search"}
									</BeegruButton> */}
								</div>
							) : null}
						</React.Fragment>
					);
				case 3:
					return (
						<React.Fragment>
							{filterTypes.includes("Bedrooms") ? (
								<BedroomsMobile
									search_id={"bedrooms_variant_2"}
									bedrooms={search_filter_state.bedrooms_variant_2}
									dispatch={dispatch}
									search_filter_state={search_filter_state}
								/>
							) : filterTypes.includes("Area") ? (
								<AreaMobile
									search_id={"area"}
									area={search_filter_state.area}
									dispatch={dispatch}
									search_filter_state={search_filter_state}
								/>
							) : filterTypes.includes("No of seats") ? (
								<NoOfSeatsMobile
									search_id={"seats"}
									search_filter_state={search_filter_state.seats}
									dispatch={dispatch}
								/>
							) : null}
							{/* <BeegruButton
								flavor="primary"
								variant="contained"
								size="medium"
								sx={{ fontSize: "1rem", alingnItems: "flex-start", height: "100%", width: "100%" }}
								onClick={handleClickSearchButton}
							>
								{router.locale === "ar-AE" ? "بحث" : router.locale === "kn-IN" ? "ಹುಡುಕಿ" : "Search"}
							</BeegruButton> */}
						</React.Fragment>
					);
			}
		} else if (filterTypes.length === 2) {
			switch (value) {
				case 0:
					return (
						<React.Fragment>
							<div style={{ marginBottom: "1rem" }}>
								<div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
									<TypographyHeading
										variant="body1"
										sx={{ marginBottom: "0.5rem" }}
									>
										{router.locale === "ar-AE"
											? "أدخل السعر"
											: router.locale === "kn-IN"
												? "ಬಜೆಟ್ ನಮೂದಿಸಿ"
												: "Enter Budget"}
									</TypographyHeading>
									<IconButton
										size="small"
										onClick={handlePopoverClose}
									>
										<CloseIcon
											fontSize="small"
											sx={{ color: "rgba(0, 0, 0, 0.54)" }}
										/>
									</IconButton>
								</div>
								<BudgetComponent
									search_id="budget"
									dispatch={dispatch}
									budget={search_filter_state.budget}
									transaction_type={search_filter_state.transaction_type}
									search_filter_state={search_filter_state}
								/>
							</div>
							{/* <BeegruButton
								flavor="primary"
								variant="contained"
								size="medium"
								sx={{ fontSize: "1rem", alingnItems: "flex-start", height: "100%", width: "100%" }}
								onClick={handleClickSearchButton}
							>
								{router.locale === "ar-AE" ? "بحث" : router.locale === "kn-IN" ? "ಹುಡುಕಿ" : "Search"}
							</BeegruButton> */}
						</React.Fragment>
					);
				case 1:
					return (
						<React.Fragment>
							{search_filter_state.search_type.selected_options[0]?.title === "Services" &&
							filterTypes[1] === "Service type" ? (
								<div style={{ flexDirection: "column", width: "100%", gap: "0.5rem" }}>
									<div style={{ padding: "0rem 0rem 0.5rem 0rem" }}>
										<div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
											<TypographyHeading variant="body1">
												{router.locale === "ar-AE"
													? "اختر فئة الخدمة"
													: router.locale === "kn-IN"
														? "ಸೇವಾ ವರ್ಗವನ್ನು ಆಯ್ಕೆಮಾಡಿ"
														: "Select service category"}
											</TypographyHeading>
											<IconButton
												size="small"
												onClick={handlePopoverClose}
											>
												<CloseIcon
													fontSize="small"
													sx={{ color: "rgba(0, 0, 0, 0.54)" }}
												/>
											</IconButton>
										</div>
										<ServiceCategoryMobile
											search_id={"service_type"}
											search_filter_state={search_filter_state.service_type}
											dispatch={dispatch}
											search_type={search_filter_state.search_type.selected_options[0]?.id as string}
										/>
									</div>
									{search_filter_state.service_type.is_applied === true && (
										<div style={{ paddingBottom: "0.5rem" }}>
											<TypographyHeading variant="body1">
												{router.locale === "ar-AE"
													? "اختر نوع الخدمة"
													: router.locale === "kn-IN"
														? "ಸೇವಾ ಪ್ರಕಾರವನ್ನು ಆಯ್ಕೆಮಾಡಿ"
														: "Select service type"}
											</TypographyHeading>
											<ServiceSubTypeMobile
												search_id={"service_subtype"}
												dispatch={dispatch}
												search_filter_state={search_filter_state.service_subtype}
												search_type={search_filter_state.search_type.selected_options[0]?.id as string}
												service_type={search_filter_state.service_type.selected_options[0]?.id as string}
											/>
										</div>
									)}
									<BeegruButton
										flavor="primary"
										variant="contained"
										size="medium"
										sx={{ fontSize: "1rem", alingnItems: "flex-start", height: "100%", width: "100%" }}
										onClick={handleClickSearchButton}
									>
										{router.locale === "ar-AE" ? "بحث" : router.locale === "kn-IN" ? "ಹುಡುಕಿ" : "Search"}
									</BeegruButton>
								</div>
							) : null}
						</React.Fragment>
					);
			}
		} else if (filterTypes.length === 1) {
			switch (value) {
				case 0:
					return (
						<React.Fragment>
							{search_filter_state.search_type.selected_options[0]?.title === "Business Profiles" &&
							filterTypes[0] === "Business user type" ? (
								<div
									style={{ flexDirection: "column", width: "100%", gap: "0.5rem", padding: "0rem 0rem 0.5rem 0rem" }}
								>
									<div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
										<TypographyHeading variant="body1">
											{router.locale === "ar-AE"
												? "اختر نوع مستخدم الأعمال"
												: router.locale === "kn-IN"
													? "ವ್ಯವಸಾಯ ಬಳಕೆದಾರರ ಪ್ರಕಾರವನ್ನು ಆಯ್ಕೆಮಾಡಿ"
													: "Select business user type"}
										</TypographyHeading>
										<IconButton
											size="small"
											onClick={handlePopoverClose}
										>
											<CloseIcon
												fontSize="small"
												sx={{ color: "rgba(0, 0, 0, 0.54)" }}
											/>
										</IconButton>
									</div>
									<FormControl>
										<FormLabel id="demo-row-radio-buttons-group-label"></FormLabel>
										<RadioGroup
											row
											aria-labelledby="demo-row-radio-buttons-group-label"
											name="row-radio-buttons-group"
											sx={{ gap: "0.5rem", fontSize: "1rem", fontWeight: 400 }}
											value={search_filter_state.business_profile_type.selected_options[0]?.value}
										>
											{search_filter_state.business_profile_type.active_options.map((data, id) => (
												<FormControlLabel
													id={"quick_search_filter_business_profile_" + id}
													key={id}
													value={data.value}
													control={<Radio />}
													label={
														data.title === "Landowners"
															? "Owners"
															: data.title === "Professionals"
																? "Service Professionals"
																: data.title
													}
													onChange={() => handleChangeBusinessUsers(data.title)}
												/>
											))}
										</RadioGroup>
									</FormControl>
									<BeegruButton
										flavor="primary"
										variant="contained"
										size="medium"
										sx={{ fontSize: "1rem", alingnItems: "flex-start", height: "100%", width: "100%" }}
										onClick={handleClickSearchButton}
									>
										{router.locale === "ar-AE" ? "بحث" : router.locale === "kn-IN" ? "ಹುಡುಕಿ" : "Search"}
									</BeegruButton>
								</div>
							) : null}
						</React.Fragment>
					);
			}
		}
	};

	React.useEffect(() => {
		if (FilterChanged) {
			PpsfFilterGenertateUrl(search_filter_state, router);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [FilterChanged]);

	return (
		<React.Fragment>
			<Container>
				{filterTypes.map((data: any, id: any) => {
					// Only hide the chip for "Property type" if the id matches the excluded list
					const excludedIds = ["warehousing", "public_infrastructure", "joint_development", "joint_venture"];
					const propertyCategoryId = search_filter_state.property_category?.selected_options[0]?.id;

					// If this is the "Property type" chip and the id is excluded, skip rendering
					if (data === "Property type" && excludedIds.includes(propertyCategoryId)) {
						return null;
					}

					// Otherwise, render the chip as usual
					if (data === null) return null;

					return (
						<PropertyChips
							id={"quick_search_filter_" + data}
							key={id}
							sx={{
								height: "2rem",
								...getStyles(data, id, search_filter_state, filterTypes.length),
								"& .MuiChip-label": {
									fontSize: "14px",
									fontWeight: getStyles(data, id, search_filter_state, filterTypes.length).fontWeight,
								},
							}}
							label={
								data === "Property type"
									? search_filter_state.property_type.selected_options.length > 0
										? `${search_filter_state.property_type.selected_options[0]?.title}`
										: router.locale === "ar-AE"
											? "نوع العقار"
											: router.locale === "kn-IN"
												? "ಆಸ್ತಿ ಪ್ರಕಾರ"
												: data
									: data === "Service type"
										? search_filter_state.service_type.selected_options.length > 0 ||
											search_filter_state.service_subtype.selected_options.length > 0
											? `${search_filter_state.service_type.selected_options[0]?.title}${
													search_filter_state.service_subtype.selected_options.length > 0
														? ` - ${search_filter_state.service_subtype.selected_options[0]?.title}`
														: ""
												}`
											: router.locale === "ar-AE"
												? "نوع الخدمة"
												: router.locale === "kn-IN"
													? "ಸೇವೆಯ ರೀತಿ"
													: data
										: data === "Business user type"
											? search_filter_state.business_profile_type.selected_options.length > 0 &&
												search_filter_state.business_profile_type.selected_options[0]?.title === "Landowners"
												? "Owners"
												: search_filter_state.business_profile_type.selected_options.length > 0 &&
													  search_filter_state.business_profile_type.selected_options[0]?.title === "Professionals"
													? "Service Professionals"
													: search_filter_state.business_profile_type.selected_options.length > 0
														? `${search_filter_state.business_profile_type.selected_options[0]?.title}`
														: router.locale === "ar-AE"
															? "نوع مستخدم الأعمال"
															: router.locale === "kn-IN"
																? "ವ್ಯವಸಾಯ ಬಳಕೆದಾರರ ರೀತಿ"
																: data
											: data === "Budget"
												? search_filter_state.budget.is_applied
													? `₹${PreparePriceString(budgetToDisplay.min)}  ${budgetToDisplay.max === 0 ? "- Max limit" : `- ₹${PreparePriceString(budgetToDisplay.max)}`}`
													: router.locale === "ar-AE"
														? "السعر"
														: router.locale === "kn-IN"
															? "ಬಜೆಟ್"
															: data
												: data === "Buy"
													? router.locale === "ar-AE"
														? "للبيع"
														: router.locale === "kn-IN"
															? "ಮಾರಾಟಕ್ಕೆ"
															: "For Buy"
													: data === "Rent"
														? router.locale === "ar-AE"
															? "للإيجار"
															: router.locale === "kn-IN"
																? "ಬಾಡಿಗೆಗೆ"
																: "For Rent"
														: data === "Resale"
															? router.locale === "ar-AE"
																? "إعادة البيع"
																: router.locale === "kn-IN"
																	? "ಮರುಮಾರಾಟ"
																	: "For Resale"
															: data
							}
							variant="outlined"
							onClick={(e) => handleChipClick(e, id)}
							deleteIcon={selectedIndex === id && openPopOver ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
							onDelete={() => {}}
						/>
					);
				})}
			</Container>
			<Popover
				open={openPopOver}
				anchorEl={anchorEl}
				onClose={handlePopoverClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				disableScrollLock={true}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				PaperProps={{
					sx: { minWidth: 320, maxWidth: 400, p: 2 },
				}}
			>
				{selectedIndex !== null && DrawerRenderFunctions(selectedIndex)}
			</Popover>
		</React.Fragment>
	);
};

export default PpsfChips;
